import React, { useEffect, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useModal } from '../context/ModalContext';
import { translations } from '../translations';
import api from '../utils/api';

const Reviews = () => {
  const { language } = useLanguage();
  const { alert } = useModal();
  const t = translations[language];
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    customerName: '',
    rating: 5,
    comment: 'Amazing service!',
    bookingId: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    fetchReviews();

    // Pre-fill user data if logged in
    const token = localStorage.getItem('token');
    if (token) {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (user && user.name) {
        setFormData(prev => ({
          ...prev,
          customerName: user.name
        }));
      }
    }
  }, []);

  const fetchReviews = async () => {
    try {
      const res = await api.get('/reviews');
      setReviews(res.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const reviewData = {
        customerName: formData.customerName.trim(),
        rating: parseInt(formData.rating),
        comment: formData.comment.trim() || undefined,
        bookingId: formData.bookingId || undefined
      };

      console.log('Submitting review:', reviewData);
      await api.post('/reviews', reviewData);
      await fetchReviews();
      setFormData({
        customerName: '',
        rating: 5,
        comment: 'Amazing service!',
        bookingId: ''
      });
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error('Error submitting review:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.details?.[0]?.msg || (language === 'en' ? 'Error submitting review' : 'خطأ في إرسال التقييم');
      await alert(
        errorMessage,
        language === 'en' ? 'Error' : 'خطأ',
        'error'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const averageRating = reviews.length > 0
    ? (reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length).toFixed(1)
    : 0;

  return (
    <div className="min-h-screen bg-blackC">
      {/* Hero Section */}
      <section className="relative pt-32 lg:pt-40">
        <div className="absolute inset-0 bg-gradient-to-b from-dark to-blackC" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <p className="section-subtitle">{t.reviewsSubtitle}</p>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-cream mb-6">
              {t.customerReviews}
            </h1>
            <div className="divider-gold mx-auto" />

            {/* Stats */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-8 mt-12">
              <div className="text-center">
                <p className="font-display text-5xl text-primary">{averageRating}</p>
                <p className="text-gray-light text-sm mt-1">{language === 'en' ? 'Average Rating' : 'متوسط التقييم'}</p>
              </div>
              <div className="hidden sm:block w-px h-16 bg-gray-dark" />
              <div className="text-center">
                <p className="font-display text-5xl text-primary">{reviews.length}</p>
                <p className="text-gray-light text-sm mt-1">{language === 'en' ? 'Total Reviews' : 'إجمالي التقييمات'}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Reviews List */}
            <div className="lg:col-span-2 space-y-6">
              {loading ? (
                <div className="flex justify-center py-20">
                  <div className="spinner" />
                </div>
              ) : reviews.length > 0 ? (
                reviews.map((review, index) => (
                  <div
                    key={review._id}
                    className="bg-dark border border-gray-dark p-8"
                  >
                    {/* Header */}
                    <div className="flex items-start justify-between mb-6">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center text-primary font-display text-xl">
                          {review.customerName?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <h4 className="text-cream font-medium">{review.customerName}</h4>
                          <p className="text-gray-light text-sm">
                            {new Date(review.createdAt).toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <svg
                            key={i}
                            className={`w-5 h-5 ${i < review.rating ? 'text-primary fill-current' : 'text-gray'}`}
                            viewBox="0 0 24 24"
                          >
                            <path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                          </svg>
                        ))}
                      </div>
                    </div>

                    {/* Comment */}
                    {review.comment && (
                      <p className="text-gray-silver leading-relaxed italic">"{review.comment}"</p>
                    )}
                  </div>
                ))
              ) : (
                <div className="text-center py-20 bg-dark border border-gray-dark">
                  <p className="text-gray-light">
                    {language === 'en' ? 'No reviews yet. Be the first to review!' : 'لا توجد تقييمات بعد. كن أول من يقيم!'}
                  </p>
                </div>
              )}
            </div>

            {/* Review Form */}
            <div className="lg:col-span-1">
              <div className="sticky top-32 bg-dark border border-gray-dark p-8">
                <h3 className="font-display text-2xl text-cream mb-6">{t.addReview}</h3>

                {showSuccess && (
                  <div className="mb-6 p-4 bg-primary/10 border border-primary/30 text-primary text-sm">
                    {language === 'en' ? 'Thank you for your review!' : 'شكراً لتقييمك!'}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm text-gray-silver mb-2">{t.name}</label>
                    <input
                      type="text"
                      value={formData.customerName}
                      onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                      className="input-luxury"
                      placeholder={language === 'en' ? 'Your name' : 'اسمك'}
                      required
                      // if user is logged in, disable the input
                      disabled={localStorage.getItem('token') ? true : false}
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-silver mb-2">{t.rating}</label>
                    <div className="flex gap-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setFormData({ ...formData, rating: star })}
                          className="p-1 transition-transform hover:scale-110"
                        >
                          <svg
                            className={`w-8 h-8 ${star <= formData.rating ? 'text-primary fill-current' : 'text-gray'}`}
                            viewBox="0 0 24 24"
                          >
                            <path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                          </svg>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-silver mb-2">{t.comment}</label>
                    <textarea
                      value={formData.comment}
                      onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                      rows="4"
                      className="textarea-luxury"
                      placeholder={language === 'en' ? 'Share your experience...' : 'شارك تجربتك...'}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full btn-primary justify-center"
                  >
                    {submitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-blackC/20 border-t-blackC rounded-full animate-spin" />
                        {language === 'en' ? 'Submitting...' : 'جاري الإرسال...'}
                      </>
                    ) : (
                      t.submit
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Reviews;